import * as React from "react";

import { InlineDuration } from "@/AgentThread/markdown/InlineDuration";
import { isEpochInstantMagnitude } from "@/AgentThread/markdown/timestampMentionDetection";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { formatBoolean } from "@/shared/text";

import styles from "./AnalyzeTopicOutput.module.css";
import {
  type JsonObject,
  type JsonValue,
  type SeamInfo,
  type ShapeFact,
  type ToonTable,
  detectAnalyzeTopicPayload,
} from "./analyzeTopicPayload";

interface TopicIdListProps {
  topicIds: string[];
}

function TopicIdList({ topicIds }: TopicIdListProps): React.JSX.Element {
  if (topicIds.length === 0) {
    return (
      <RobotoTypography component="p" className={styles.topicIdsEmpty}>
        No topics matched
      </RobotoTypography>
    );
  }
  return (
    <div className={styles.topicIds}>
      {topicIds.map((id) => (
        <span key={id} className={styles.topicIdChip}>
          {id}
        </span>
      ))}
    </div>
  );
}

/**
 * A nanosecond duration shown as readable text (`923.000 ms`), with the raw ns
 * on hover. A value that is not an integer string (a float, `—`) renders
 * verbatim, and so does an epoch instant: a `*_ns` name also fits one
 * (`MIN(_roboto_timestamp) AS first_ns`), which as a duration reads "20503d".
 */
function Duration({ raw }: { raw: string }): React.JSX.Element {
  if (!/^[-+]?\d+$/.test(raw) || isEpochInstantMagnitude(BigInt(raw))) {
    return <>{raw}</>;
  }
  return <InlineDuration ns={BigInt(raw)} />;
}

function renderCell(
  value: string | boolean | null,
  column: string,
): React.ReactNode {
  if (value === null) {
    return <span className={styles.nullCell}>—</span>;
  }
  if (typeof value === "boolean") {
    return formatBoolean(value);
  }
  if (column.endsWith("_ns")) {
    return <Duration raw={value} />;
  }
  return value;
}

/** A fact value as text: scalars verbatim, arrays and objects as JSON, missing or null as `—`. */
function factText(value: JsonValue | undefined): string {
  if (value === undefined || value === null) {
    return "—";
  }
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}

function renderFactValue(key: string, value: JsonValue): React.ReactNode {
  if (value === null) {
    return <span className={styles.nullCell}>—</span>;
  }
  if (Array.isArray(value)) {
    return factText(value);
  }
  if (typeof value === "object") {
    return <FactFields fields={value} />;
  }
  if (key.endsWith("_ns")) {
    return <Duration raw={String(value)} />;
  }
  return String(value);
}

/** Each field of a facts object as `key value`, nested objects rendered the same way. */
function FactFields({ fields }: { fields: JsonObject }): React.JSX.Element {
  return (
    <span className={styles.factFields}>
      {Object.entries(fields).map(([key, value]) => (
        <span key={key}>
          <span className={styles.factKey}>{key}</span>{" "}
          {renderFactValue(key, value)}
        </span>
      ))}
    </span>
  );
}

const SHAPE_FIELDS = ["start", "n", "min", "avg", "max"];

/** One table per column of `shape`, one row per equal-time bucket. */
function ShapeTables({ shape }: { shape: ShapeFact }): React.JSX.Element {
  return (
    <>
      {Object.entries(shape.columns).map(([column, buckets]) => (
        <div key={column} className={styles.tableScroll}>
          <table className={styles.table}>
            <caption className={styles.shapeCaption}>
              <div className={styles.factLine}>
                <span className={styles.factName}>shape of {column}</span>
                {shape.bucketNs !== undefined && (
                  <span>
                    <span className={styles.factKey}>bucket</span>{" "}
                    <Duration raw={factText(shape.bucketNs)} />
                  </span>
                )}
              </div>
            </caption>
            <thead>
              <tr>
                {SHAPE_FIELDS.map((field) => (
                  <th key={field}>{field}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {buckets.map((bucket, index) => (
                <tr key={index}>
                  {SHAPE_FIELDS.map((field) => (
                    <td key={field}>
                      {bucket[field] === null ? (
                        <span className={styles.nullCell}>—</span>
                      ) : (
                        factText(bucket[field])
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </>
  );
}

interface FactsProps {
  spans?: JsonObject;
  profile?: Record<string, JsonObject>;
  profileOmitted?: string[];
  values?: Record<string, JsonObject[]>;
  shape?: ShapeFact;
}

function Facts({
  spans,
  profile,
  profileOmitted,
  values,
  shape,
}: FactsProps): React.JSX.Element | null {
  if (!spans && !profile && !profileOmitted && !values && !shape) {
    return null;
  }
  const profileFields = profile
    ? Array.from(new Set(Object.values(profile).flatMap(Object.keys)))
    : [];
  return (
    <div className={styles.facts}>
      {spans &&
        Object.entries(spans).map(([name, span]) => (
          <div key={name} className={styles.factLine}>
            <span className={styles.factName}>{name}</span>
            {renderFactValue(name, span)}
          </div>
        ))}
      {profile && (
        <div className={styles.tableScroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>profile</th>
                {profileFields.map((field) => (
                  <th key={field}>{field}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(profile).map(([column, entry]) => (
                <tr key={column}>
                  <td>{column}</td>
                  {profileFields.map((field) => (
                    <td key={field}>
                      {field in entry
                        ? renderFactValue(field, entry[field])
                        : null}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {shape && <ShapeTables shape={shape} />}
      {profileOmitted && (
        <RobotoTypography component="p" className={styles.note}>
          not profiled: {profileOmitted.join(", ")}
        </RobotoTypography>
      )}
      {values &&
        Object.entries(values).map(([column, entries]) => (
          <div key={column} className={styles.factLine}>
            <span className={styles.factName}>{column}</span>
            <span className={styles.topicIds}>
              {entries.map((entry, index) => (
                <span
                  key={index}
                  className={styles.topicIdChip}
                  title={`first_at ${factText(entry.first_at)}, last_at ${factText(entry.last_at)}`}
                >
                  {`${factText(entry.value)} ×${factText(entry.n)}`}
                </span>
              ))}
            </span>
          </div>
        ))}
    </div>
  );
}

interface ToonTableViewProps {
  table: ToonTable;
}

function ToonTableView({ table }: ToonTableViewProps): React.JSX.Element {
  if (table.columns.length === 0 && table.rows.length === 0) {
    return (
      <RobotoTypography component="p" className={styles.emptyTable}>
        Query returned no columns.
      </RobotoTypography>
    );
  }
  return (
    <div className={styles.tableScroll}>
      <table className={styles.table}>
        <thead>
          <tr>
            {table.columns.map((col, idx) => (
              <th key={`${col}-${idx}`}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, rIdx) => (
            <tr key={rIdx}>
              {table.columns.map((column, cIdx) => (
                <td key={cIdx}>{renderCell(row[cIdx] ?? null, column)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SeamDivider({ seam }: { seam: SeamInfo }): React.JSX.Element {
  return (
    <RobotoTypography component="p" className={styles.seam}>
      {`${seam.omitted} rows omitted`}
      {seam.from !== undefined && seam.to !== undefined && (
        <>
          {` (${seam.from} → ${seam.to}`}
          {seam.durationNs !== undefined && (
            <>
              {", "}
              <Duration raw={seam.durationNs} />
            </>
          )}
          {")"}
        </>
      )}
    </RobotoTypography>
  );
}

interface AnalyzeTopicOutputProps {
  output: unknown;
}

/**
 * Renderer for the ``Output`` section of an analyze_topic tool result. Returns
 * ``null`` if the payload doesn't match any known shape — the caller then
 * falls back to the generic JSON view so nothing is hidden if the contract
 * ever shifts under us.
 */
export function AnalyzeTopicOutput({
  output,
}: AnalyzeTopicOutputProps): React.JSX.Element | null {
  const payload = React.useMemo(
    () => detectAnalyzeTopicPayload(output),
    [output],
  );

  if (payload.kind === "text") {
    return <pre className={styles.errorText}>{payload.text}</pre>;
  }

  if (payload.kind === "unknown") {
    return null;
  }

  const { topicIds, table } = payload;
  const toon = payload.kind === "toon" ? payload : undefined;
  const total = toon?.totalMatchingRows;
  const tailRows = toon?.tailTable?.rowCount;
  let dataLabel = `data (${table.rowCount} ${table.rowCount === 1 ? "row" : "rows"})`;
  if (total !== undefined) {
    dataLabel =
      tailRows !== undefined
        ? `data (first ${table.rowCount} and last ${tailRows} of ${total} matching rows)`
        : `data (first ${table.rowCount} of ${total} matching rows)`;
  }
  return (
    <div className={styles.container}>
      <div className={styles.topicIdsSection}>
        <RobotoTypography component="p" className={styles.subLabel}>
          topic_ids ({topicIds.length})
        </RobotoTypography>
        <TopicIdList topicIds={topicIds} />
      </div>
      {toon && (
        <Facts
          spans={toon.spans}
          profile={toon.profile}
          profileOmitted={toon.profileOmitted}
          values={toon.values}
          shape={toon.shape}
        />
      )}
      <div className={styles.tableSection}>
        <RobotoTypography component="p" className={styles.subLabel}>
          {dataLabel}
        </RobotoTypography>
        {toon?.note !== undefined && (
          <RobotoTypography component="p" className={styles.note}>
            {toon.note}
          </RobotoTypography>
        )}
        <ToonTableView table={table} />
        {toon?.seam && <SeamDivider seam={toon.seam} />}
        {toon?.tailTable && <ToonTableView table={toon.tailTable} />}
        {toon?.trailingText !== undefined && (
          <RobotoTypography component="p" className={styles.note}>
            {toon.trailingText}
          </RobotoTypography>
        )}
      </div>
    </div>
  );
}
